import { supabase } from '../lib/supabaseClient';
import { Technician } from '@/types';
import type { TechnicianRow } from '@/types/supabase';
import { logger } from '../utils/logger';
import { getOfflineCache, setOfflineCache } from '../lib/offlineCache';
import { isNetworkError } from '../utils/network';

export const TechnicianService = {
    async getTechnicians(): Promise<Technician[]> {
        try {
            const { data, error } = await supabase.from('technicians').select('*');
            if (error) throw error;

            const result: Technician[] = (data || []).map((t: TechnicianRow) => ({
                id: t.id,
                authUserId: t.auth_user_id,
                companyId: t.company_id,
                name: t.name,
                email: t.email || undefined,
                phone: t.phone || undefined,
                category: t.category || 'General',
                specialties: t.specialties || [],
                status: (t.status || 'active') as Technician['status'],
                role: (t.role || 'technician') as Technician['role'],
                avatar: t.avatar_url || '/default-avatar-v14.png',
                color: t.color || '#3b82f6',
                currentLocation: t.current_lat != null ? { lat: t.current_lat, lng: t.current_lng || 0, address: '' } : undefined
            }));

            await setOfflineCache('pd_cached_technicians', result);
            return result;
        } catch (err) {
            if (isNetworkError(err)) {
                const cached = await getOfflineCache('pd_cached_technicians');
                if (cached) {
                    logger.info('TechnicianService: Restored technicians from offline cache');
                    return cached;
                }
            }
            throw err;
        }
    },

    async createTechnician(technician: Technician, companyId: string): Promise<void> {
        if (!companyId) throw new Error("Company ID missing in call");

        const targetEmail = technician.email?.toLowerCase().trim();
        if (targetEmail) {
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            if (currentUser?.email && currentUser.email.toLowerCase().trim() === targetEmail) {
                throw new Error('El administrador de la empresa ya tiene cuenta y no puede ser añadido como técnico.');
            }
        }

        const { error } = await supabase.rpc('create_technician_secure', {
            p_name: technician.name,
            p_email: technician.email?.toLowerCase(),
            p_company_id: companyId,
            p_role: 'technician',
            p_status: 'pending',
            p_category: technician.category,
            p_specialties: technician.specialties,
            p_color: technician.color,
            p_avatar_url: technician.avatar,
            p_phone: technician.phone
        });

        if (error) {
            logger.error("RPC create_technician_secure failed", error);
            throw new Error(`Error al guardar (RPC): ${error.message}`);
        }
    },

    async updateTechnician(technician: Technician): Promise<void> {
        const targetEmail = technician.email?.toLowerCase().trim();
        if (targetEmail && technician.role !== 'admin') {
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            if (currentUser?.email && currentUser.email.toLowerCase().trim() === targetEmail) {
                throw new Error('El administrador de la empresa ya tiene cuenta y no puede ser asignado como técnico.');
            }
            if (technician.companyId) {
                const { data: comp } = await supabase
                    .from('companies')
                    .select('admin_email')
                    .eq('id', technician.companyId)
                    .maybeSingle();
                if (comp?.admin_email && comp.admin_email.toLowerCase().trim() === targetEmail) {
                    throw new Error('El administrador de la empresa ya tiene cuenta y no puede ser asignado como técnico.');
                }
            }
        }

        const { error, data } = await supabase
            .from('technicians')
            .update({
                name: technician.name,
                company_id: technician.companyId,
                category: technician.category,
                specialties: technician.specialties,
                status: technician.status,
                color: technician.color,
                avatar_url: technician.avatar,
                email: technician.email,
                phone: technician.phone
            })
            .eq('id', technician.id)
            .select();

        if (error) throw error;
        if (!data || data.length === 0) {
            throw new Error("Error: No se pudo actualizar el técnico. Verifique permisos.");
        }
    },

    async deleteTechnician(technicianId: string): Promise<void> {
        // Calls a SECURITY DEFINER RPC that:
        //  1. Validates the caller is an admin of the same company.
        //  2. Deletes the technician row.
        //  3. Deletes the corresponding auth.users entry so they can no longer log in.
        const { error } = await supabase.rpc('delete_technician_and_auth_user', {
            p_technician_id: technicianId,
        });
        if (error) throw new Error(`Error al eliminar técnico: ${error.message}`);
    }
};
